import { generateClientNameString } from '@utils/generateClientNameString';
import type { TFunction } from 'i18next';
import type { ReconFlagRow, ReconFlagRowUser } from '../utils/reconFlags.types';
import {
  ACTIVITY_ORDER,
  RESERVED_ROOT_KEYS,
  SYSTEM_ID_ORDER,
} from './reconFlagDrawerOverviewConstants';
import {
  formatPrimitiveDetail,
  isPlainObject,
  readString,
  toSentenceCaseLabel,
} from './reconFlagDrawerOverviewFormatting';
import type {
  ReconFlagOverviewRow,
  ReconFlagOverviewSection,
} from './reconFlagDrawerOverviewTypes';

export const collectEntriesInOrder = (
  source: Record<string, unknown>,
  order: readonly string[],
  appendRemainingScalars: boolean,
): { key: string; value: unknown }[] => {
  const seen = new Set<string>();
  const rows: { key: string; value: unknown }[] = [];
  for (const k of order) {
    if (k in source && source[k] !== undefined && source[k] !== null) {
      rows.push({ key: k, value: source[k] });
      seen.add(k);
    }
  }
  if (!appendRemainingScalars) {
    return rows;
  }
  for (const k of Object.keys(source)) {
    if (seen.has(k) || RESERVED_ROOT_KEYS.has(k)) {
      continue;
    }
    if (source[k] === undefined || source[k] === null) {
      continue;
    }
    if (typeof source[k] === 'object' && !Array.isArray(source[k])) {
      continue;
    }
    rows.push({ key: k, value: source[k] });
  }
  return rows;
};

export const userDisplayName = (
  user: ReconFlagRowUser | null | undefined,
): string | undefined => {
  if (!user) {
    return undefined;
  }
  return generateClientNameString(user, false, true) || undefined;
};

export const mergeAccountShape = (
  flag: ReconFlagRow,
): Record<string, unknown> | null => {
  const details = flag.details;
  const fromDetails = isPlainObject(details) ? details.account : undefined;
  const detailAccount = isPlainObject(fromDetails) ? fromDetails : {};

  const merged: Record<string, unknown> = { ...detailAccount };

  const fed = flag.account;
  if (fed?.custodianAccountNumber) {
    merged.custodianAccountNumber ??= fed.custodianAccountNumber;
  }
  const fedName = userDisplayName(fed?.user ?? null);
  if (fedName) {
    merged.clientName ??= fedName;
  }

  const detailUser = isPlainObject(detailAccount.user)
    ? (detailAccount.user as ReconFlagRowUser)
    : undefined;
  const detailUserName = userDisplayName(detailUser ?? null);
  if (detailUserName) {
    merged.clientName ??= detailUserName;
  }

  if (typeof details === 'object' && details !== null) {
    const d = details as Record<string, unknown>;
    merged.clientName ??= readString(d.clientName);
    merged.custodianName ??= readString(d.custodianName);
    merged.custodian ??= readString(d.custodian);
  }

  const keys = Object.keys(merged).filter(
    (k) => merged[k] !== undefined && merged[k] !== null && merged[k] !== '',
  );
  if (keys.length === 0 && !fed?.user && !fed?.custodianAccountNumber) {
    return null;
  }
  return merged;
};

const hasFederatedAccount = (flag: ReconFlagRow): boolean =>
  Boolean(flag.account?.user && flag.account?.custodianAccountNumber);

export const shouldShowAccountSection = (flag: ReconFlagRow): boolean => {
  const merged = mergeAccountShape(flag);
  if (merged && Object.keys(merged).some((k) => k !== 'user')) {
    return true;
  }
  return hasFederatedAccount(flag);
};

type ActivitySourceInfo = {
  record: Record<string, unknown>;
  appendRemainingScalars: boolean;
};

export const resolveActivitySource = (
  flag: ReconFlagRow,
): ActivitySourceInfo | null => {
  const details = flag.details;
  if (!isPlainObject(details)) {
    return null;
  }
  if (isPlainObject(details.activity)) {
    return {
      record: details.activity as Record<string, unknown>,
      appendRemainingScalars: true,
    };
  }
  return { record: details, appendRemainingScalars: false };
};

export const activityKeysPresent = (src: Record<string, unknown>): boolean => {
  for (const k of ACTIVITY_ORDER) {
    if (k in src && src[k] !== undefined && src[k] !== null) {
      return true;
    }
  }
  return false;
};

type SystemSourceInfo = {
  record: Record<string, unknown>;
  appendRemainingScalars: boolean;
};

export const resolveSystemSource = (
  flag: ReconFlagRow,
): SystemSourceInfo | null => {
  const details = flag.details;
  if (!isPlainObject(details)) {
    return null;
  }
  if (isPlainObject(details.systemIdentifiers)) {
    return {
      record: details.systemIdentifiers as Record<string, unknown>,
      appendRemainingScalars: true,
    };
  }
  if (isPlainObject(details.system_identifiers)) {
    return {
      record: details.system_identifiers as Record<string, unknown>,
      appendRemainingScalars: true,
    };
  }
  return { record: details, appendRemainingScalars: false };
};

export const systemKeysPresent = (src: Record<string, unknown>): boolean => {
  for (const k of SYSTEM_ID_ORDER) {
    if (k in src && src[k] !== undefined && src[k] !== null) {
      return true;
    }
  }
  return false;
};

export const issueSummaryText = (flag: ReconFlagRow): string | undefined => {
  const details = flag.details;
  if (isPlainObject(details)) {
    const d = details as Record<string, unknown>;
    const fromIssue =
      readString(d.issueSummary) ??
      readString(d.issue_summary) ??
      readString(d.summary) ??
      readString(d.description);
    if (fromIssue) {
      return fromIssue;
    }
  }
  return readString(flag.message);
};

export const headerSubtitleParts = (flag: ReconFlagRow): string | undefined => {
  const details = flag.details;
  if (isPlainObject(details)) {
    const d = details as Record<string, unknown>;
    const explicit =
      readString(d.issueTitle) ??
      readString(d.issue_title) ??
      readString(d.displayType) ??
      readString(d.display_type) ??
      readString(d.humanReadableType);
    if (explicit) {
      return explicit;
    }
  }
  return readString(flag.objectType) ?? readString(flag.type);
};

const currencyHintFrom = (src: Record<string, unknown>): string | undefined => {
  const c = readString(src.currency) ?? readString(src.baseCurrency);
  return c;
};

const fieldLabel = (t: TFunction, technicalKey: string): string => {
  const map: Record<string, string> = {
    transactionType: 'table.transactionType',
    transaction_type: 'table.transactionType',
    symbol: 'symbol',
    quantity: 'table.quantity',
    price: 'table.price',
    commission: 'table.commission',
    accruedInterest: 'table.accruedInterest',
    accrued_interest: 'table.accruedInterest',
    amount: 'table.amount',
    currency: 'table.currency',
    tradeDate: 'table.date',
    trade_date: 'table.date',
    settleDate: 'table.settleDate',
    settle_date: 'table.settleDate',
    accountType: 'table.accountType',
    account_type: 'table.accountType',
    baseCurrency: 'table.currency',
    base_currency: 'table.currency',
    accountStatus: 'reconFlags.drawer.fields.accountStatus',
    account_status: 'reconFlags.drawer.fields.accountStatus',
    custodianAccountNumber: 'table.custodianAccountNumber',
    custodian_account_number: 'table.custodianAccountNumber',
    custodianName: 'table.custodian',
    custodian_name: 'table.custodian',
    custodian: 'table.custodian',
    clientName: 'table.clientName',
    client_name: 'table.clientName',
    transactionId: 'reconFlags.drawer.fields.transactionId',
    transaction_id: 'reconFlags.drawer.fields.transactionId',
    subTransactionId: 'reconFlags.drawer.fields.subTransactionId',
    sub_transaction_id: 'reconFlags.drawer.fields.subTransactionId',
    subTransId: 'reconFlags.drawer.fields.subTransactionId',
    sub_trans_id: 'reconFlags.drawer.fields.subTransactionId',
    cusip: 'table.cusip',
    CUSIP: 'table.cusip',
    financialProductId: 'reconFlags.drawer.fields.financialProductId',
    financial_product_id: 'reconFlags.drawer.fields.financialProductId',
    finProductId: 'reconFlags.drawer.fields.financialProductId',
    fin_product_id: 'reconFlags.drawer.fields.financialProductId',
    status: 'reconFlags.drawer.fields.activityStatus',
    type: 'reconFlags.drawer.fields.activityType',
  };
  const path = map[technicalKey];
  if (path) {
    return t(path);
  }
  return toSentenceCaseLabel(technicalKey);
};

export const rowsFromOrderedObject = (
  t: TFunction,
  source: Record<string, unknown>,
  order: readonly string[],
  appendRemainingScalars: boolean,
): ReconFlagOverviewRow[] => {
  const hint = currencyHintFrom(source);
  const entries = collectEntriesInOrder(source, order, appendRemainingScalars);
  return entries.map(({ key, value }) => ({
    label: fieldLabel(t, key),
    value: formatPrimitiveDetail(t, key, value, hint),
  }));
};

export const buildAdditionalSections = (
  t: TFunction,
  flag: ReconFlagRow,
  consumedRootKeys: Set<string>,
): ReconFlagOverviewSection[] => {
  const details = flag.details;
  if (!isPlainObject(details)) {
    return [];
  }

  const sections: ReconFlagOverviewSection[] = [];

  for (const key of Object.keys(details)) {
    if (consumedRootKeys.has(key)) {
      continue;
    }
    const value = details[key];
    if (value === undefined || value === null) {
      continue;
    }
    if (!isPlainObject(value)) {
      continue;
    }

    const inner = value as Record<string, unknown>;
    const rows: ReconFlagOverviewRow[] = Object.keys(inner)
      .filter((ik) => inner[ik] !== undefined && inner[ik] !== null)
      .sort((a, b) => a.localeCompare(b))
      .map((ik) => ({
        label: fieldLabel(t, ik),
        value: formatPrimitiveDetail(t, ik, inner[ik], currencyHintFrom(inner)),
      }));

    if (rows.length > 0) {
      sections.push({
        id: `nested-${key}`,
        heading: toSentenceCaseLabel(key),
        rows,
      });
    }
  }

  const shallowRows: ReconFlagOverviewRow[] = [];
  for (const key of Object.keys(details).sort((a, b) => a.localeCompare(b))) {
    if (consumedRootKeys.has(key)) {
      continue;
    }
    const value = details[key];
    if (value === undefined || value === null) {
      continue;
    }
    if (isPlainObject(value)) {
      continue;
    }
    if (Array.isArray(value)) {
      shallowRows.push({
        label: fieldLabel(t, key),
        value: value
          .map((v) => formatPrimitiveDetail(t, key, v, undefined))
          .join(', '),
      });
      continue;
    }
    shallowRows.push({
      label: fieldLabel(t, key),
      value: formatPrimitiveDetail(
        t,
        key,
        value,
        currencyHintFrom(details as Record<string, unknown>),
      ),
    });
  }

  if (shallowRows.length > 0) {
    sections.push({
      id: 'additional-shallow',
      heading: t('reconFlags.drawer.sections.additional'),
      rows: shallowRows,
    });
  }

  return sections;
};
